/**
 * Category Matcher Module - Phase 3 Migration
 * Handles category and date range match conditions
 */

"use strict";

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);
const debugMode = urlParams.get('debug') || '0';

import { qRep } from '../utils/regex-utils.js';
import { escapeForCategory } from './query-parser/query-parser.js';

/**
 * Add category match conditions
 * @param {Array} conditions - Search conditions to modify
 * @param {Array} categories - Categories to add
 */
export function addCategoryMatchConditions(conditions, categories) {
    
    // CRITICAL: Use window.search_categories if categories parameter is not provided
    if (!categories && window.search_categories) {
        categories = window.search_categories;
    }
    
    // CRITICAL: Ensure conditions is an array
    if (!Array.isArray(conditions)) {
        conditions = [];
    }
    
    if (!categories || !Array.isArray(categories) || categories.length === 0) {
        // If UI has active category selections but mapping produced no category IDs,
        // enforce a no-match condition to avoid showing all documents.
        try {
            const af = (window.filterController && typeof window.filterController.getActiveFilters === 'function')
                ? window.filterController.getActiveFilters()
                : null;
            const hasCategoryUiSelections = !!(af && af.categories && Object.values(af.categories)
                .some(arr => Array.isArray(arr) && arr.length > 0));
            if (hasCategoryUiSelections) {
                conditions.push({
                    condition: 'and',
                    value: [{ condition: 'or', value: ['a^'], modifier: '' }],
                    isCategory: true
                });
            }
        } catch(_) { /* noop */ }
        return;
    }
    
    if (!conditions || !Array.isArray(conditions)) {
        return;
    }
    
    // Keep original categories count to detect the case when user selected categories
    // but mapping produced no concrete search patterns
    const originalCategoriesCount = Array.isArray(categories) ? categories.length : 0;
    let searches = [];
    for (let i in categories) {
        let s = "";
        // categories[i] may already be a pattern (from category-filters)
        let v = categories[i];
        if (window.CATEG2 && categories[i] in window.CATEG2) {
            v = window.CATEG2[categories[i]];
        }
        if (!v) continue;
        
        s += (s == "" ? v : "*" + v);
        
        // Preserve '*' and '/' from CATEG2 patterns
        s = qRep(escapeForCategory(s), true);

        
        // Handle multiple patterns separated by semicolon
        if (s.search(/;/) !== -1) {
            let searches2 = s.split(';');
            for (let j in searches2) {
                let pat = searches2[j];
                if (pat && pat[0] !== '/') pat = '/' + pat; // ensure leading slash for full-path match
                searches.push(pat);
            }
        } else {
            let pat = s;
            if (pat && pat[0] !== '/') pat = '/' + pat; // ensure leading slash for full-path match
            searches.push(pat);
        }
    }
    // If user explicitly selected categories but we couldn't build any search patterns
    // (e.g., combination not present in CATEG/CATEG2), enforce a no-match condition
    // so that the result is an empty set instead of silently ignoring category filters.
    if (originalCategoriesCount > 0 && searches.length === 0) {
        try { console.log('[Categories] No patterns produced from selection; enforcing no-match.'); } catch(_) {}
        conditions.push({
            condition: 'and',
            value: [{ condition: 'or', value: ['a^'], modifier: '' }],
            isCategory: true
        });
        return;
    }

    if (searches.length > 0) {
        try { console.log('[Categories] Built patterns:', searches.slice(0, 12)); } catch(_) {}
        try {
            if (debugMode === '1') {
                const sample = searches.slice(0, 3);
                console.log('[addCategoryMatchConditions] pattern sample', sample);
            }
        } catch(_) { /* noop */ }
        // Add category condition to match conditions; mark it as full-path category block
        conditions.push({ 
            "condition": "and", 
            "value": [{ 
                "condition": "or", 
                "value": searches, 
                "modifier": "i" 
            }], 
            "isCategory": true,
            "isCategoryFull": true 
        });
    }

    // === Date range conditions (ported from legacy main.js) ===
    try {
        const $from = window.jQuery ? window.jQuery('.filter-date-from') : null;
        const $to = window.jQuery ? window.jQuery('.filter-date-to') : null;
        let sf = $from && $from.length ? String($from.val() || '') : '';
        let st = $to && $to.length ? String($to.val() || '') : '';

        // Normalize inputs: keep only digits and dashes, support YYYY->YY
        const normalizeYY = (s) => {
            if (!s) return '';
            s = s.replace(/[^0-9\-]/g, '').trim();
            // Strict numeric YY-MM-DD / YYYY-MM-DD; reject non-digit segments to avoid NaN downstream.
            const m = s.match(/^(\d{2}|\d{4})-(\d{2})-(\d{2})$/);
            if (!m) return s;
            const yy = m[1].length === 4 ? m[1].slice(-2) : m[1];
            return `${yy}-${m[2]}-${m[3]}`;
        };
        sf = normalizeYY(sf);
        st = normalizeYY(st);

        if (sf || st) {
            const RP = window.RegexPatterns || window.searchPatterns || {};
            const RI = RP._internal || {};
            const DateFromTo = RP.DateFromTo || RP.DateFromTo;
            if (DateFromTo && RI.rFromTo && RI.dateRangePatterns && RI.monthReplace) {
                const dF = new DateFromTo(sf || '50-01-01', true);
                const dT = new DateFromTo(st || '99-12-31', false);

                const allDays = "(?:[0-2]\\d|3[01])";
                const allMonths = "(?:0[1-9]|1[0-2])";
                const rFromTo = RI.rFromTo;
                const padBack = RI.padBack;
                const dateRangePatterns = RI.dateRangePatterns;

                let dateCond = [];

                // Current year-month block from start day to either end day or 31
                const currenMonthDayStart = dF.day;
                const currenMonthDayEnd = (dF.year === dT.year && dT.month === dF.month ? dT.day : '31');
                dateCond.push(...dateRangePatterns(
                    dF.year,
                    [dF.month, dF.monthN],
                    rFromTo(currenMonthDayStart, currenMonthDayEnd)
                ));

                // Remaining months in the start year until either end month-1 or 12
                const currentYearMonthStart = (dF.month < '12' ? padBack(dF.monthN + 1) : '');
                if (currentYearMonthStart) {
                    const currentYearMonthEnd = (dF.year === dT.year ? (dT.monthN - dF.monthN > 1 ? padBack(dT.monthN - 1) : '') : '12');
                    if (currentYearMonthEnd) {
                        dateCond.push(...dateRangePatterns(
                            dF.year,
                            [rFromTo(currentYearMonthStart, currentYearMonthEnd), currentYearMonthStart, currentYearMonthEnd],
                            allDays
                        ));
                    }
                }

                // Full middle years between from and to
                const midYearStart = (dT.yearN - dF.yearN > 1 ? padBack(dF.yearN + 1) : '');
                if (midYearStart) {
                    const midYearEnd = padBack(dT.yearN - 1);
                    dateCond.push(...dateRangePatterns(
                        rFromTo(midYearStart, midYearEnd),
                        [allMonths, 1, 12],
                        allDays
                    ));
                }

                // Last year months up to end month/day
                const lastYearStart = (dT.yearN - dF.yearN > 0 ? dT.year : '');
                if (lastYearStart) {
                    if (dT.monthN > 1) {
                        dateCond.push(...dateRangePatterns(
                            dT.year,
                            [rFromTo('01', padBack(dT.monthN - 1)), 1, dT.monthN - 1],
                            allDays
                        ));
                    }
                    dateCond.push(...dateRangePatterns(
                        dT.year,
                        [dT.month, dT.month],
                        rFromTo('01', dT.day)
                    ));
                }

                if (Array.isArray(dateCond) && dateCond.length > 0) {
                    conditions.push({
                        condition: 'and',
                        value: [{ condition: 'or', value: dateCond, modifier: 'i' }]
                    });
                    if (window.HyperLog) { try { window.HyperLog.debug('[SearchConditions] Date conditions added', { count: dateCond.length, from: sf, to: st }); } catch(_) { /* noop */ } }
                }
            }
        }
    } catch(_) { /* noop */ }
}

